import { number, plural } from '@frameio/i18n';
import {
  getSystemFieldDisplayName,
  SYSTEM_FIELD_KEYS,
  type SystemFieldKey,
} from '@frameio/shared/fields/utils';
import type { AssetResultMatch } from '../types';
import TEXT from './generated/TEXT/MatchLabelMap';

TEXT.DEFINE({
  /** Search label for updated date matches. */
  updatedAtMatch: 'Date Updated',
  /** Search label for deleted date matches. */
  deletedAtMatch: 'Date Deleted',
  /** Search label for inserted date matches. */
  insertedAtMatch: 'Date Inserted',
  /** Search label for visual search matches. */
  visualMatch: ({ count }: { count: number }): string =>
    plural(count, `Visual Match (${number(count)})`, 'Visual Match'),
  /** Search label for codec matches. */
  codecMatch: 'Codec',
  /** Search label for transcript text matches. */
  transcriptionResultMatch: 'Transcript',
  /** Search label for comment count matches (shorter form for search UI). */
  commentCountMatch: 'Comments',
  /** Search label for page count matches (shorter form for search UI). */
  pageCountMatch: 'Pages',
});

// Search callers already know the exact system-field key from __typename.
const systemLabel = (key: SystemFieldKey): string =>
  getSystemFieldDisplayName(key);

const searchOnlyMatchLabels = {
  UpdatedAtMatch: () => TEXT.updatedAtMatch,
  DeletedAtMatch: () => TEXT.deletedAtMatch,
  InsertedAtMatch: () => TEXT.insertedAtMatch,
  VisualMatch: (params) => TEXT.visualMatch(params as { count: number }),
  CodecMatch: () => TEXT.codecMatch,
  // @hack see FeaturedFieldMatches.tsx for more details
  TranscriptionResultMatch: () => TEXT.transcriptionResultMatch,
} as const satisfies Partial<
  Record<
    NonNullable<AssetResultMatch['__typename']>,
    (params?: Record<string, any>) => string
  >
>;

const sharedFieldMatchLabels = {
  AssigneeMatch: () => systemLabel(SYSTEM_FIELD_KEYS.ASSIGNEE),
  AudioSampleRateMatch: () => systemLabel(SYSTEM_FIELD_KEYS.AUDIO_SAMPLE_RATE),
  BitDepthMatch: () => systemLabel(SYSTEM_FIELD_KEYS.VISUAL_BIT_DEPTH),
  BitRateMatch: () => systemLabel(SYSTEM_FIELD_KEYS.BIT_RATE),
  ChannelsMatch: () => systemLabel(SYSTEM_FIELD_KEYS.AUDIO_CHANNELS),
  ColorSpaceMatch: () => systemLabel(SYSTEM_FIELD_KEYS.COLOR_SPACE),
  CommentCountMatch: () => TEXT.commentCountMatch,
  CreatorMatch: () => systemLabel(SYSTEM_FIELD_KEYS.UPLOADER),
  DurationMatch: () => systemLabel(SYSTEM_FIELD_KEYS.DURATION),
  DynamicRangeMatch: () => systemLabel(SYSTEM_FIELD_KEYS.DYNAMIC_RANGE),
  FileTypeMatch: () => systemLabel(SYSTEM_FIELD_KEYS.FILE_TYPE),
  FilesizeMatch: () => systemLabel(SYSTEM_FIELD_KEYS.FILE_SIZE),
  FormatMatch: () => systemLabel(SYSTEM_FIELD_KEYS.FORMAT),
  FrameRateMatch: () => systemLabel(SYSTEM_FIELD_KEYS.FRAME_RATE),
  HasAlphaChannelMatch: () => systemLabel(SYSTEM_FIELD_KEYS.ALPHA_CHANNEL),
  KeywordsMatch: () => systemLabel(SYSTEM_FIELD_KEYS.KEYWORDS),
  NameMatch: () => systemLabel(SYSTEM_FIELD_KEYS.NAME),
  NotesMatch: () => systemLabel(SYSTEM_FIELD_KEYS.NOTES),
  PageCountMatch: () => TEXT.pageCountMatch,
  RatingMatch: () => systemLabel(SYSTEM_FIELD_KEYS.RATING),
  ResolutionHeightMatch: () => systemLabel(SYSTEM_FIELD_KEYS.RESOLUTION_HEIGHT),
  ResolutionWidthMatch: () => systemLabel(SYSTEM_FIELD_KEYS.RESOLUTION_WIDTH),
  SourceFilenameMatch: () => systemLabel(SYSTEM_FIELD_KEYS.SOURCE_FILENAME),
  StatusMatch: () => systemLabel(SYSTEM_FIELD_KEYS.STATUS),
  TranscriptMatch: () => systemLabel(SYSTEM_FIELD_KEYS.TRANSCRIPT),
  UploadedAtMatch: () => systemLabel(SYSTEM_FIELD_KEYS.DATE_UPLOADED),
} as const satisfies Partial<
  Record<
    NonNullable<AssetResultMatch['__typename']>,
    (params?: Record<string, any>) => string
  >
>;

export const MatchLabelMap: Partial<
  Record<
    NonNullable<AssetResultMatch['__typename']>,
    (params?: Record<string, any>) => string
  >
> = {
  ...searchOnlyMatchLabels,
  ...sharedFieldMatchLabels,
};
